import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { getContainersFromClients } from 'components/attacks/create/targets/target-utils'
import {
  getProvenanceByTargetType,
  isTargetParamsEmpty
} from 'components/services/service-utils'
import { TargetingStrategy } from 'types/api/attacks/details/targeting'
import { ProcessProvenance } from 'types/api/services/service'
import { TargetParams } from 'types/forms'

export const HOST_TARGETING_SECTION_COUNT = 6

// These are reversed so that when they are reversed later they
// are in the correct order.
export const HOST_TARGETING_SECTION_DEFAULT_NAMES = [
  'region',
  'zone',
  'service',
  'os-type'
]

export const HOST_TARGETING_SECTION_DISPLAY_NAMES = {
  'os-type': 'Operating Systems',
  zone: 'Zone',
  region: 'Region',
  service: 'Service'
}

// To preserve order
export const KUBERNETES_TARGETING_SECTION_NAMES = [
  'DEPLOYMENT',
  'DAEMONSET',
  'REPLICASET',
  'STATEFULSET',
  'POD'
]

// Dictionary for section display names
export const KUBERNETES_TARGETING_SECTION_DISPLAY_NAMES = {
  DEPLOYMENT: 'Deployments',
  DAEMONSET: 'DaemonSets',
  REPLICASET: 'ReplicaSets',
  STATEFULSET: 'StatefulSets',
  POD: 'Pods'
}

/**
 * These utility functions are sourced from functions of the same name found in
 * 'components/attacks/create/targets/target-utils.js` and
 * 'components/attacks/create/targets/kubernetes/kubernetes-target-utils.js'.
 *
 * They have been migrated to use TypeScript with new predefined types as well as refactored to eliminate
 * unnecessary parameters that are not utilized in the new RM Create Service workflow.
 */

function formatTargetParams(targetParams: TargetParams) {
  return Object.entries(targetParams).reduce((params, [key, values]) => {
    if (values.size === 0) return params // Value is empty; skip

    // Converting from Set() to array for API endpoint
    params[key] = Array.from(values)

    return params
  }, {})
}

/**
 * Returns a boolean indicating whether a Host or Container target is selected
 *
 * @param target
 * @param targetType
 * @param selectedTargets
 * @param selectedTargetParams
 * @param targetAll
 */
export const isTargetSelected = (
  target,
  targetType,
  selectedTargets = [],
  selectedTargetParams = {},
  targetAll = false
) => {
  if (selectedTargets.length === 0) {
    return false
  }

  const params =
    targetType === ProvenanceTargetType.ProcessData
      ? target.tags
      : target.labels

  // If there are no target params with selected values, all targets are considered selected
  // But we have switched our UI to show none selected by default to "start safe"
  // This is overriden later on with the "Target all hosts" option
  // This creates a default state of no targets being selected
  if (targetAll) {
    if (isTargetParamsEmpty(selectedTargetParams)) return true
  } else {
    if (isTargetParamsEmpty(selectedTargetParams)) return false
  }

  return Object.entries(selectedTargetParams).every(
    ([key, values = new Set()]) => {
      if (values instanceof Set) {
        if (values.size === 0) return true // skip empty sets
        return values.has(params[key])
      } else {
        return params && params[key] === values
      }
    }
  )
}

/**
 * Returns a list of targetable services from a list of Clients.
 *
 * @param targets
 * @param targetType
 * @param selectedTargetParams
 */
export const formatTagsAndLabels = (
  targets,
  targetType,
  selectedTargetParams = {}
) => {
  let idKey
  let paramsKey
  let availableTargets

  if (targetType === ProvenanceTargetType.Container) {
    idKey = 'id'
    paramsKey = 'labels'
    availableTargets = getContainersFromClients(targets)
  } else {
    idKey = 'identifier'
    paramsKey = 'tags'
    availableTargets = targets
  }

  // Grab targets that match current selection
  const selectedTargets = availableTargets.filter((target) =>
    isTargetSelected(target, targetType, [], selectedTargetParams, false)
  )

  // Create singular set of alike tags/labels
  const combined = selectedTargets.reduce((combinedParams, target) => {
    const params = target[paramsKey]
    Object.entries(params).forEach(([key, value]) => {
      if (!combinedParams[key]) {
        combinedParams[key] = new Set()
      }

      combinedParams[key].add(value)
    })

    return combinedParams
  }, {})

  const out = availableTargets.reduce(
    ({ keyValueSets, labelKeys }, target) => {
      const id = target[idKey]
      const params = target[paramsKey]

      Object.entries(params).forEach(([key, value]) => {
        if (!labelKeys[key]) {
          labelKeys[key] = {
            name: key,
            targets: [],
            values: new Set(),
            disabled: new Set()
          }
        }

        // Add key-value to the set
        keyValueSets.add(`${key}:"${value}"`)
        labelKeys[key].values.add(value)
        labelKeys[key].targets.push(id)

        // If no target params have been selected yet; skip adding anything to the disabled list
        if (isTargetParamsEmpty(selectedTargetParams)) {
          return
        }

        if (combined[key] && combined[key].has(value)) {
          labelKeys[key].disabled.delete(value)
        } else {
          labelKeys[key].disabled.add(value)
        }
      })
      return { keyValueSets, labelKeys }
    },
    {
      keyValueSets: new Set(),
      labelKeys: {}
    }
  )

  return { availableTargetParams: out.keyValueSets, labelKeys: out.labelKeys }
}

/**
 * Returns the clients that would be targeted for either host or container based attacks
 *
 * @param clients
 * @param targetType
 * @param selectedTargets
 * @param targetParams
 * @param targetAll
 * @returns {[]} selected clients
 */
export const getSelectedClients = (
  clients,
  targetType,
  selectedTargets,
  targetParams,
  targetAll
) => {
  return clients.reduce((accum, target) => {
    if (targetType === ProvenanceTargetType.ProcessData) {
      const isClientSelected = isTargetSelected(
        target,
        targetType,
        selectedTargets,
        targetParams,
        targetAll
      )

      if (isClientSelected) {
        accum.push(target)
      }
    } else if (targetType === ProvenanceTargetType.Container) {
      const { containers } = target
      const isContainerSelected = containers.some((container) =>
        isTargetSelected(
          container,
          targetType,
          selectedTargets,
          targetParams,
          targetAll
        )
      )

      if (isContainerSelected) {
        accum.push(target)
      }
    }
    return accum
  }, [])
}

/**
 * Returns correct tag/label format based on the type of selection strategy being used
 * @NOTE Currently based on `multiselection-targeting-enabled` LD feature flag
 */
export const getTargetParamsKey = (targetType) => {
  if (targetType === ProvenanceTargetType.ProcessData) {
    return 'multiSelectTags'
  } else if (targetType === ProvenanceTargetType.Container) {
    return 'multiSelectLabels'
  } else {
    return 'tags'
  }
}

/**
 * Returns a boolean indicating whether a Kubernetes target is selected.
 *
 * @param targets
 * @param selectedNamespace
 * @param selectedObjects
 */
export function getSelectedKubernetesTargets(
  targets,
  { selectedNamespace, selectedObjects }
) {
  return targets.filter((t) => {
    const isTargetSelected = selectedObjects.has(t.uid)

    if (isTargetSelected) {
      if (selectedNamespace && selectedNamespace.toLowerCase() !== 'all') {
        return t.namespace === selectedNamespace
      }
    }

    return isTargetSelected
  })
}

/**
 * Returns a targetingStrategy for Host and/or Container target types
 *
 * @param targetType
 * @param targetParams
 */
export function getTargetingStrategy(
  targetType: ProvenanceTargetType,
  targetParams: TargetParams
): TargetingStrategy {
  const provenance: ProcessProvenance = getProvenanceByTargetType(targetType)

  return {
    type: ProvenanceTargetType[provenance],
    [getTargetParamsKey(targetType)]: formatTargetParams(targetParams)
  }
}

/**
 * Returns a targetingStrategy for Kubernetes target types
 *
 * @param k8sClientsFlat
 * @param formValues
 */
export function getKubernetesTargetingStrategy(
  k8sClientsFlat: Record<string, any>,
  formValues: {
    selectedNamespace: string
    selectedObjects: Set<string>
  }
): TargetingStrategy {
  return {
    type: ProvenanceTargetType.Kubernetes,
    names: getSelectedKubernetesTargets(k8sClientsFlat, formValues).map(
      ({ name, namespace, kind, clusterId }) => ({
        name,
        namespace,
        kind,
        clusterId
      })
    )
  }
}
